import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { MaterialSymbol } from 'react-material-symbols';

import useParent from '../../../hooks/useParent.js';
import AuthLayout from '../../../components/AuthLayout.js';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.tsx';
import { Input } from '../../../components/ui/input.tsx';

// Todo: remove outline on input with smaller screen size

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(
        /[^a-zA-Z0-9]/,
        'Password must contain at least one special character'
      ),
    confirmPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(
        /[^a-zA-Z0-9]/,
        'Password must contain at least one special character'
      ),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Passwords do not match',
        path: ['confirmPassword'],
      });
    }
  });

const ParentDashboardPasswordReset = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });
  const {
    parentResetPassword,
    parentResetPasswordLoading,
    parentResetPasswordSuccess,
  } = useParent();

  const onSubmit = async (values) => {
    await parentResetPassword({
      token,
      newPassword: values.password,
    });
  };

  useEffect(() => {
    let timer;
    if (parentResetPasswordSuccess) {
      timer = setTimeout(() => {
        navigate('/parent/login');
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [parentResetPasswordSuccess, navigate]);

  return (
    <AuthLayout title="Password Reset">
      <Form {...form}>
        {parentResetPasswordSuccess && (
          <div className="p-3 text-sm text-green-600 bg-green-50 rounded-md">
            <p>You will be redirected to login in 5 seconds.</p>
          </div>
        )}
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col h-2/5 justify-between"
        >
          <div className="flex flex-col gap-y-10">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#4d4d4d] text-lg font-light">
                    Enter your new password
                  </FormLabel>
                  <FormControl>
                    <span className="flex items-center border border-[#e5e5e5] rounded-sm h-10 px-2">
                      <MaterialSymbol
                        icon="lock"
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                      />
                      <Input
                        type={`${showPassword ? 'text' : 'password'}`}
                        placeholder=""
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none rounded-none h-7"
                      />
                      <MaterialSymbol
                        icon={`${
                          showPassword ? 'visibility' : 'visibility_off'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowPassword(!showPassword);
                        }}
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                        className="cursor-pointer"
                      />
                    </span>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#4d4d4d] text-lg font-light">
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <span className="flex items-center border border-[#e5e5e5] rounded-sm h-10 px-2">
                      <MaterialSymbol
                        icon="lock"
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                      />
                      <Input
                        type={`${showConfirmPassword ? 'text' : 'password'}`}
                        placeholder=""
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none rounded-none h-7"
                      />
                      <MaterialSymbol
                        icon={`${
                          showConfirmPassword ? 'visibility' : 'visibility_off'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                        className="cursor-pointer"
                      />
                    </span>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <button
            type="submit"
            disabled={parentResetPasswordLoading}
            className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 justify-self-end"
          >
            {parentResetPasswordLoading ? 'Loading' : 'Reset Password'}
          </button>
        </form>
      </Form>
    </AuthLayout>
  );
};

export default ParentDashboardPasswordReset;
